/**
 * Disposable Email Detection
 * 
 * Uses the community-maintained `disposable-email-domains` package
 * which contains 3000+ known disposable/temporary email domains.
 * 
 * The list is regularly updated by the community:
 * https://github.com/disposable-email-domains/disposable-email-domains
 */

import domains from 'disposable-email-domains';

// Convert array to Set for O(1) lookup performance
const DISPOSABLE_DOMAINS: Set<string> = new Set(domains);

/**
 * Check if an email address uses a disposable/temporary email domain
 * 
 * @param email - The email address to check
 * @returns true if the email is from a disposable domain, false otherwise
 */
export function isDisposableEmail(email: string): boolean {
    if (!email || typeof email !== 'string') {
        return false;
    }

    const parts = email.toLowerCase().trim().split('@');
    if (parts.length !== 2) {
        return false;
    }

    const domain = parts[1];

    // Check exact domain match
    if (DISPOSABLE_DOMAINS.has(domain)) {
        return true;
    }

    // Check subdomain match (e.g., subdomain.mailinator.com)
    const domainParts = domain.split('.');
    for (let i = 1; i < domainParts.length; i++) {
        const parentDomain = domainParts.slice(i).join('.');
        if (DISPOSABLE_DOMAINS.has(parentDomain)) {
            return true;
        }
    }

    return false;
}

/**
 * Get the domain from an email address
 * 
 * @param email - The email address
 * @returns The domain part of the email, or null if invalid
 */
export function getEmailDomain(email: string): string | null {
    if (!email || typeof email !== 'string') {
        return null;
    }

    const parts = email.toLowerCase().trim().split('@');
    if (parts.length !== 2) {
        return null;
    }

    return parts[1];
}
